import React, { useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ModalTitle, RecaptchaField } from 'components';
import { checkPasswordStrength } from 'helpers/validation';
import axios from 'helpers/axios';
import { RESET_PASSWORD } from 'constants/apis';
import { ToastContext } from 'contexts';
import {
  useCustomErrorCode,
  CUSTOM_ERROR_CODE,
  useAccountLockout,
  useFeatureFlags,
} from 'helpers/hooks';
import { Box, Link, Modal, Form, TextButton, useMediaQuery } from 'bonfire-kindling';

const PasswordChangeModal = ({ onClose, open }) => {
  const isMobileWidth = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const { triggerToast } = useContext(ToastContext);
  const { getCustomError } = useCustomErrorCode();
  const { triggerAccountLockout } = useAccountLockout();
  const { isFlagEnabled } = useFeatureFlags();

  const onSubmit = async ({ currentPassword, newPassword, confirmPassword, recaptchaToken }) => {
    let errors = {};
    const isPasswordStrong = await checkPasswordStrength(newPassword);
    if (!isPasswordStrong) {
      errors.newPassword = (
        // These two strings are split into seperate fragments so that the &nbsp; will be rendered.
        // It does not show if the message is stored as a single translation.
        <>
          {t('login.you_cannot_use_this_password_error')}&nbsp;
          <Trans i18nKey="login.why_am_i_seeing_this_error">
            <Link
              target="_blank"
              rel="noopener noreferrer"
              href={
                isFlagEnabled('FF_15_RELEASE_TOGGLE_UPDATE_PV_SUPPORT_LINKS')
                  ? 'https://vendorsupport.gobonfire.com/hc/en-us/articles/6831723396247-Why-am-I-seeing-a-You-cannot-use-this-password-error-'
                  : 'https://support.gobonfire.com/hc/en-us/articles/4409780940183'
              }
            >
              Why am I seeing this error?
            </Link>
          </Trans>
        </>
      );
    }
    if (newPassword !== confirmPassword) {
      errors.confirmPassword = t('settings.change_password.no_match');
    }
    if (!recaptchaToken) {
      errors.recaptchaToken = t('settings.change_password.recaptcha_required');
    }
    if (Object.keys(errors).length > 0) {
      return errors;
    }

    return axios
      .patch(RESET_PASSWORD, {
        currentPassword,
        password: newPassword,
        recaptchaToken,
      })
      .then(() => {
        triggerToast({
          message: t('settings.change_password.success'),
          variant: 'success',
        });
        onClose();
      })
      .catch(error => {
        const { message, code } = getCustomError(error);

        if (code === CUSTOM_ERROR_CODE.LOCKED_OUT) {
          triggerAccountLockout();
        }

        triggerToast({ message, variant: 'error' });
      });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="password-change-modal-title"
      size={isMobileWidth ? 'tiny' : 'medium'}
    >
      <ModalTitle onClose={onClose} id="password-change-modal-title">
        {t('settings.change_password.title')}
      </ModalTitle>
      <Form onSubmit={onSubmit}>
        {({ handleSubmit, submitting }) => (
          <form onSubmit={handleSubmit}>
            <Box
              padding={isMobileWidth ? '0 1rem' : '0 2rem'}
              display="flex"
              flexDirection="column"
              alignItems={isMobileWidth ? 'center' : 'flex-start'}
            >
              <Form.TextField
                required
                label={t('settings.change_password.current')}
                name="currentPassword"
                type="password"
                fullWidth
              />
              <Form.TextField
                label={t('settings.change_password.enter')}
                name="newPassword"
                type="password"
                required
                fullWidth
              />
              <Form.TextField
                label={t('settings.change_password.confirm')}
                name="confirmPassword"
                type="password"
                required
                fullWidth
              />
              <Form.Field name="recaptchaToken" component={RecaptchaField} />
            </Box>
            <Box
              display="flex"
              padding={isMobileWidth ? '1rem 1rem' : '1rem 2rem'}
              justifyContent="end"
            >
              <TextButton
                variant="contained"
                color="primary"
                type="submit"
                isLoading={submitting}
                label={t('settings.change_password.change')}
                sx={{ marginRight: '1rem' }}
              />
              <TextButton
                variant="outlined"
                onClick={onClose}
                disabled={submitting}
                label={t('settings.change_password.cancel')}
              />
            </Box>
          </form>
        )}
      </Form>
    </Modal>
  );
};

export default PasswordChangeModal;
